import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { AuthBanner, AuthCard, AuthLink, AuthSubmit } from "@/components/auth/AuthCard.jsx";
import Button from "@/components/ui/Button.jsx";
import { Field, IconInput, PasswordInput } from "@/components/ui/Form.jsx";
import Icon from "@/components/ui/Icon.jsx";
import { organizationService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { cx } from "@/lib/cx.js";

/*
 * Replaces frontend/app/(auth)/register/page.js — markup from register.html.
 *
 * The payload is FLAT and its field names are the ones the backend destructures
 * in organizationController.createOrganization: name, identifier, adminName,
 * email, password, and the four S3 fields. confirmPassword is stripped.
 */

const S3_FIELDS = ["bucketName", "accessKey", "secretKey", "region"];

const schema = z
    .object({
        name: z.string().trim().min(1, "Please enter your organization name"),
        identifier: z
            .string()
            .trim()
            .min(1, "Please enter an organization identifier")
            .regex(/^[a-z0-9-]+$/, "Only lowercase letters, numbers and hyphens are allowed"),
        adminName: z.string().trim().min(1, "Please enter the admin name"),
        email: z
            .string()
            .trim()
            .min(1, "Please enter the admin email")
            .email("Please enter a valid email address"),
        password: z.string().min(8, "Password must be at least 8 characters long"),
        confirmPassword: z.string().min(1, "Please confirm your password"),
        bucketName: z.string().trim().default(""),
        accessKey: z.string().trim().default(""),
        secretKey: z.string().trim().default(""),
        region: z.string().trim().default(""),
    })
    .superRefine((values, ctx) => {
        if (values.confirmPassword && values.confirmPassword !== values.password) {
            ctx.addIssue({
                path: ["confirmPassword"],
                code: "custom",
                message: "The two passwords do not match",
            });
        }

        /*
         * All-or-nothing S3.
         *
         * Not a nicety: the backend returns 400 for a partial config, and a
         * partial config that somehow got through would silently break
         * screenshot upload for the whole organization. Flagging the empty
         * fields here says which ones are missing instead of a single opaque
         * server error.
         */
        const filled = S3_FIELDS.filter((f) => values[f]);
        if (filled.length && filled.length < S3_FIELDS.length) {
            for (const f of S3_FIELDS) {
                if (!values[f]) {
                    ctx.addIssue({
                        path: [f],
                        code: "custom",
                        message: "Required when any other S3 field is filled in",
                    });
                }
            }
        }
    });

const STEPS = [
    { caption: "Organization", fields: ["name", "identifier"] },
    { caption: "Admin account", fields: ["adminName", "email", "password", "confirmPassword"] },
    { caption: "Storage", fields: S3_FIELDS },
];

/* ---- Password strength, ported from register.js ------------------------- */
const STRENGTH = [
    { label: "Too short", bar: "bg-rose-500", text: "text-rose-600 dark:text-rose-400", hint: " — use at least 8 characters" },
    { label: "Weak", bar: "bg-rose-500", text: "text-rose-600 dark:text-rose-400", hint: " — add numbers or symbols" },
    { label: "Fair", bar: "bg-amber-500", text: "text-amber-600 dark:text-amber-400", hint: " — mix upper and lower case" },
    { label: "Good", bar: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400", hint: " — add a symbol for extra strength" },
    { label: "Strong", bar: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400", hint: "" },
];

function scorePassword(pw) {
    if (pw.length < 8) return 0;
    let score = 1;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    return Math.min(score, 4);
}

/** Organization name → a usable identifier, until the user types their own. */
const slugify = (value) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

export default function Register() {
    const navigate = useNavigate();

    const [step, setStep] = useState(0);
    const [banner, setBanner] = useState(null);
    const [done, setDone] = useState(null); // the created org, once registered
    const [s3, setS3] = useState(null); // null | validating | valid | invalid | skipped
    const [s3Detail, setS3Detail] = useState("");
    const [identifierTouched, setIdentifierTouched] = useState(false);

    const {
        register,
        handleSubmit,
        trigger,
        control,
        setValue,
        getValues,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: zodResolver(schema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            name: "",
            identifier: "",
            adminName: "",
            email: "",
            password: "",
            confirmPassword: "",
            bucketName: "",
            accessKey: "",
            secretKey: "",
            region: "",
        },
    });

    // useWatch, not watch(): watch() returns a fresh function every render, which
    // React Compiler cannot memoize, so it bails out of optimising the whole
    // component. useWatch subscribes to just these two fields.
    const password = useWatch({ control, name: "password" });
    const identifier = useWatch({ control, name: "identifier" });
    const strength = STRENGTH[scorePassword(password || "")];

    /* Only validate the fields on THIS step — zod validates the whole object,
       so the later steps' errors are simply ignored until we reach them. */
    const goNext = async () => {
        const ok = await trigger(STEPS[step].fields, { shouldFocus: true });
        if (ok) setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };

    const validateS3 = async () => {
        const values = getValues();
        const filled = S3_FIELDS.filter((f) => values[f]);

        if (!filled.length) {
            setS3("skipped");
            setS3Detail(
                "No S3 fields filled in — screenshots will be stored on our servers. You can add a bucket later in organization settings.",
            );
            return;
        }

        if (!(await trigger(S3_FIELDS))) {
            setS3("invalid");
            setS3Detail("Fill in all four fields, or clear them all to skip this step.");
            return;
        }

        setS3("validating");
        setS3Detail("Testing the credentials and bucket access…");
        try {
            const res = await organizationService.validateS3Config({
                bucketName: values.bucketName,
                accessKey: values.accessKey,
                secretKey: values.secretKey,
                region: values.region,
            });
            const good = res?.data?.status === "success";
            setS3(good ? "valid" : "invalid");
            setS3Detail(
                good
                    ? "The bucket is reachable and writable. You can finish registration."
                    : res?.data?.message || "Check the credentials, the region and the bucket policy.",
            );
        } catch (error) {
            setS3("invalid");
            setS3Detail(apiErrorMessage(error, "Check the credentials, the region and the bucket policy."));
        }
    };

    const onSubmit = async (values) => {
        setBanner(null);
        // confirmPassword is a UI-only field; everything else goes as-is.
        const { confirmPassword: _drop, ...payload } = values;

        try {
            await organizationService.createOrganization(payload);
            setDone({ name: payload.name, identifier: payload.identifier });
        } catch (error) {
            setBanner(apiErrorMessage(error, "Registration failed. Please try again."));
            // The failure is almost always a duplicate identifier or email, both
            // on step 1 — send them where the fix is rather than leaving them on
            // the storage step wondering.
            if (/identifier|email|exists/i.test(apiErrorMessage(error, ""))) setStep(0);
        }
    };

    if (done) return <SuccessCard org={done} onSignIn={() => navigate("/login")} />;

    const last = step === STEPS.length - 1;

    return (
        <AuthCard
            eyebrow="Get started"
            title="Create your organization"
            subtitle="Three short steps. You can change everything except the identifier later."
            width="max-w-[520px]"
            footer={
                <p className="text-[13px] text-slate-500 dark:text-slate-400">
                    Already have an account? <AuthLink to="/login">Sign in</AuthLink>
                </p>
            }
        >
            <Stepper step={step} />

            <AuthBanner tone="error">{banner}</AuthBanner>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Step 0 — organization */}
                <div className={cx("space-y-4", step !== 0 && "hidden")} data-step="0">
                    <Field label="Organization name" htmlFor="name" error={errors.name?.message}>
                        <IconInput
                            id="name"
                            autoComplete="organization"
                            placeholder="HasTech IT"
                            icon={<Icon name="team" className="size-4.5" />}
                            {...register("name", {
                                onChange: (e) => {
                                    if (!identifierTouched) {
                                        setValue("identifier", slugify(e.target.value));
                                    }
                                },
                            })}
                        />
                    </Field>

                    <Field
                        label="Organization identifier"
                        htmlFor="identifier"
                        error={errors.identifier?.message}
                        hint={
                            <>
                                Lowercase letters, numbers and hyphens only.{" "}
                                {identifier && (
                                    <>
                                        Employees will enter{" "}
                                        <code className="rounded bg-slate-100 px-1 py-px font-mono text-[11px] text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                            {identifier}
                                        </code>
                                    </>
                                )}
                            </>
                        }
                    >
                        <IconInput
                            id="identifier"
                            className="font-mono"
                            autoCapitalize="none"
                            spellCheck={false}
                            placeholder="hastech-it"
                            icon={<Icon name="book" className="size-4.5" />}
                            {...register("identifier", {
                                onChange: () => setIdentifierTouched(true),
                            })}
                        />
                    </Field>

                    <p className="text-[12px] leading-relaxed text-slate-400 dark:text-slate-500">
                        Employees type the identifier into the desktop app to join your
                        organization. <strong className="font-semibold">It cannot be changed later.</strong>
                    </p>
                </div>

                {/* Step 1 — admin account */}
                <div className={cx("space-y-4", step !== 1 && "hidden")} data-step="1">
                    <Field label="Admin name" htmlFor="adminName" error={errors.adminName?.message}>
                        <IconInput
                            id="adminName"
                            autoComplete="name"
                            placeholder="Mahamudur Rahaman"
                            icon={<Icon name="user" className="size-4.5" />}
                            {...register("adminName")}
                        />
                    </Field>

                    <Field
                        label="Admin email"
                        htmlFor="email"
                        error={errors.email?.message}
                        hint="This becomes the first dashboard login."
                    >
                        <IconInput
                            id="email"
                            type="email"
                            autoComplete="email"
                            placeholder="admin@company.com"
                            icon={<Icon name="user" className="size-4.5" />}
                            {...register("email")}
                        />
                    </Field>

                    <Field label="Password" htmlFor="password" error={errors.password?.message}>
                        <PasswordInput
                            id="password"
                            autoComplete="new-password"
                            placeholder="At least 8 characters"
                            icon={<Icon name="shield" className="size-4.5" />}
                            {...register("password")}
                        />
                        {password && (
                            <div className="mt-2" data-strength>
                                <div className="flex gap-1">
                                    {[0, 1, 2, 3].map((i) => (
                                        <span
                                            key={i}
                                            className={cx(
                                                "h-1 flex-1 rounded-full transition",
                                                (password.length >= 8 ? i < scorePassword(password) : i === 0)
                                                    ? strength.bar
                                                    : "bg-slate-200 dark:bg-slate-800",
                                            )}
                                        />
                                    ))}
                                </div>
                                <p className="mt-1.5 text-[11.5px]">
                                    <span className={cx("font-medium", strength.text)}>
                                        {strength.label}
                                    </span>
                                    <span className="text-slate-400 dark:text-slate-500">
                                        {strength.hint}
                                    </span>
                                </p>
                            </div>
                        )}
                    </Field>

                    <Field
                        label="Confirm password"
                        htmlFor="confirmPassword"
                        error={errors.confirmPassword?.message}
                    >
                        <PasswordInput
                            id="confirmPassword"
                            autoComplete="new-password"
                            placeholder="Re-enter the password"
                            icon={<Icon name="shield" className="size-4.5" />}
                            {...register("confirmPassword")}
                        />
                    </Field>
                </div>

                {/* Step 2 — storage */}
                <div className={cx("space-y-4", step !== 2 && "hidden")} data-step="2">
                    <div className="flex items-start gap-2.5 rounded-lg border border-brand-100 bg-brand-50/60 px-3.5 py-3 dark:border-brand-900 dark:bg-brand-950/40">
                        <span className="mt-px shrink-0 text-brand-500">
                            <Icon name="shield" className="size-4.5" />
                        </span>
                        <p className="text-[12.5px] leading-relaxed text-slate-600 dark:text-slate-300">
                            <strong className="font-semibold">This whole step is optional.</strong>{" "}
                            Screenshots are stored on our servers by default. Provide an S3 bucket if
                            you would rather keep them in your own AWS account — you can also set this
                            up later in organization settings.
                        </p>
                    </div>

                    <Field label="S3 bucket name" htmlFor="bucketName" optional error={errors.bucketName?.message}>
                        <IconInput
                            id="bucketName"
                            className="font-mono"
                            autoCapitalize="none"
                            spellCheck={false}
                            placeholder="my-organization-bucket"
                            icon={<Icon name="folder" className="size-4.5" />}
                            {...register("bucketName")}
                        />
                    </Field>

                    <Field label="AWS access key ID" htmlFor="accessKey" optional error={errors.accessKey?.message}>
                        <IconInput
                            id="accessKey"
                            className="font-mono"
                            autoCapitalize="none"
                            spellCheck={false}
                            placeholder="AKIA…"
                            icon={<Icon name="shield" className="size-4.5" />}
                            {...register("accessKey")}
                        />
                    </Field>

                    <Field label="AWS secret access key" htmlFor="secretKey" optional error={errors.secretKey?.message}>
                        <PasswordInput
                            id="secretKey"
                            className="font-mono"
                            autoComplete="off"
                            placeholder="••••••••••••••••"
                            icon={<Icon name="shield" className="size-4.5" />}
                            {...register("secretKey")}
                        />
                    </Field>

                    <Field label="AWS region" htmlFor="region" optional error={errors.region?.message}>
                        <IconInput
                            id="region"
                            className="font-mono"
                            autoCapitalize="none"
                            spellCheck={false}
                            placeholder="ap-southeast-1"
                            icon={<Icon name="grid" className="size-4.5" />}
                            {...register("region")}
                        />
                    </Field>

                    <div className="flex items-center gap-3">
                        <Button
                            type="button"
                            onClick={validateS3}
                            loading={s3 === "validating"}
                            data-validate-s3
                        >
                            Test this configuration
                        </Button>
                        <span className="text-[12px] text-slate-400 dark:text-slate-500">
                            Optional, but it fails loudly here instead of silently later.
                        </span>
                    </div>

                    {s3 && <S3Status kind={s3} detail={s3Detail} />}
                </div>

                {/* Navigation */}
                <div className="mt-7 flex items-center gap-3">
                    {step > 0 && (
                        <Button type="button" data-back onClick={() => setStep((s) => s - 1)} className="h-12">
                            Back
                        </Button>
                    )}

                    {last ? (
                        <div className="flex-1">
                            <AuthSubmit data-create loading={isSubmitting} loadingLabel="Creating…">
                                Create organization
                            </AuthSubmit>
                        </div>
                    ) : (
                        <div className="flex-1">
                            {/* type=button: Enter on step 0 must advance, not submit
                                a half-filled organization. */}
                            <AuthSubmit type="button" data-next onClick={goNext}>
                                Continue
                            </AuthSubmit>
                        </div>
                    )}
                </div>

                <p className="mt-3 text-center text-[12px] text-slate-400 dark:text-slate-500">
                    Step {step + 1} of {STEPS.length}
                </p>
            </form>
        </AuthCard>
    );
}

/* ---- Stepper ------------------------------------------------------------ */
function Stepper({ step }) {
    return (
        <ol className="mb-7 flex items-start" data-stepper>
            {STEPS.map((s, i) => {
                const done = i < step;
                const active = i === step;

                return (
                    <li key={s.caption} className="flex flex-1 flex-col items-center gap-1.5">
                        <div className="flex w-full items-center">
                            <span className={cx("h-px flex-1", i === 0 && "invisible", i <= step ? "bg-brand-500" : "bg-slate-200 dark:bg-slate-700")} />
                            <span
                                data-step-circle={active ? "active" : done ? "done" : "todo"}
                                className={cx(
                                    "grid size-8 shrink-0 place-items-center rounded-full text-[13px] font-semibold transition",
                                    done && "bg-emerald-500 text-white",
                                    active &&
                                        "bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-[0_4px_12px_-4px_rgba(99,102,241,0.7)]",
                                    !done &&
                                        !active &&
                                        "border border-slate-200 bg-white text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500",
                                )}
                            >
                                {done ? (
                                    <svg
                                        className="size-4"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                    >
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                ) : (
                                    i + 1
                                )}
                            </span>
                            <span className={cx("h-px flex-1", i === STEPS.length - 1 && "invisible", i < step ? "bg-brand-500" : "bg-slate-200 dark:bg-slate-700")} />
                        </div>

                        <span
                            className={cx(
                                "text-center text-[11.5px] leading-tight",
                                active && "font-semibold text-brand-600 dark:text-brand-400",
                                done && "font-medium text-emerald-600 dark:text-emerald-400",
                                !done && !active && "font-medium text-slate-400 dark:text-slate-500",
                            )}
                        >
                            {s.caption}
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}

/* ---- S3 validation result ----------------------------------------------- */
const S3_STATUS = {
    validating: {
        box: "border-brand-200 bg-brand-50/60 dark:border-brand-900 dark:bg-brand-950/40",
        title: "text-brand-700 dark:text-brand-300",
        heading: "Validating S3 configuration…",
    },
    valid: {
        box: "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40",
        title: "text-emerald-700 dark:text-emerald-300",
        heading: "S3 configuration valid",
    },
    invalid: {
        box: "border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40",
        title: "text-rose-700 dark:text-rose-300",
        heading: "S3 configuration invalid",
    },
    skipped: {
        box: "border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/40",
        title: "text-slate-700 dark:text-slate-200",
        heading: "Nothing to validate",
    },
};

function S3Status({ kind, detail }) {
    const s = S3_STATUS[kind];
    if (!s) return null;

    return (
        <div
            data-s3-status={kind}
            className={cx("flex items-start gap-2.5 rounded-lg border px-3.5 py-3", s.box)}
        >
            <div>
                <span className={cx("block text-[13px] font-semibold", s.title)}>{s.heading}</span>
                <span className="mt-0.5 block text-[12px] leading-relaxed text-slate-500 dark:text-slate-400">
                    {detail}
                </span>
            </div>
        </div>
    );
}

/* ---- Success ------------------------------------------------------------ */
function SuccessCard({ org, onSignIn }) {
    const steps = [
        <>
            Sign in and invite your team from <strong className="font-semibold">Users</strong>.
        </>,
        <>
            Install the desktop tracker and enter the identifier{" "}
            <code className="rounded bg-slate-100 px-1 py-px font-mono text-[11.5px] text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {org.identifier}
            </code>
            .
        </>,
        <>Set tracking schedules and productivity rules.</>,
    ];

    return (
        <div className="w-full max-w-[520px] animate-fade-up" data-registered>
            <div className="rounded-2xl border border-slate-200/70 bg-white p-9 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-12px_rgba(15,23,42,0.10)] dark:border-slate-800 dark:bg-slate-900">
                <span className="mx-auto grid size-14 place-items-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                    <svg
                        className="size-7"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M20 6 9 17l-5-5" />
                    </svg>
                </span>

                <h1 className="mt-5 text-[22px] font-bold tracking-tight">Organization created</h1>
                <p className="mx-auto mt-2 max-w-sm text-[14px] leading-relaxed text-slate-500 dark:text-slate-400">
                    <strong className="font-semibold text-slate-700 dark:text-slate-200">
                        {org.name}
                    </strong>{" "}
                    is ready. Sign in with the admin account you just created.
                </p>

                <ol className="mt-6 space-y-2.5 text-left">
                    {steps.map((content, i) => (
                        <li
                            key={i}
                            className="flex items-start gap-3 rounded-lg border border-slate-200 px-3.5 py-3 dark:border-slate-800"
                        >
                            <span className="grid size-6 shrink-0 place-items-center rounded-md bg-brand-50 text-[11.5px] font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-200">
                                {i + 1}
                            </span>
                            <span className="text-[13px] text-slate-600 dark:text-slate-300">
                                {content}
                            </span>
                        </li>
                    ))}
                </ol>

                <button
                    type="button"
                    onClick={onSignIn}
                    className="btn btn-gradient mt-7 h-11 w-full text-[15px]"
                >
                    Sign in to your dashboard
                </button>
            </div>
        </div>
    );
}
